import React, { useEffect, useState } from "react";
import { api } from "../api/api";
import { messaggioDelServer } from "../utils/enrolDati";
// La veste della finestra «Event payment» (Fetta 7), la stessa: guscio §8
// (AdminTools.css), card con titolo oro (PaymentsModal.css), riquadro
// d'errore e righe della scheda (ClinicEvents.css). Nessun foglio modificato
// salvo le aggiunte in coda a ClinicEvents.css.
import "../components/AdminTools.css";
import "../components/PaymentsModal.css";
import "./ClinicEvents.css";

/**
 * Clinic / Off Season / Specials — la finestra «Guest details» di UN ospite
 * iscritto a UN evento. Cantiere CLINICS / OFFSEASON, Fetta G1 «registrazione
 * guest» (24/9/2026). La apre il bottone «View Guest» della riga del
 * partecipante, nella pagina dell'evento (ClinicEventDetail.jsx), sulle sole
 * righe degli ospiti.
 *
 * ⚠️ SOLA LETTURA. Nessun campo modificabile, nessun gesto: il solo comando è
 * «Close». Un ospite non ha una scheda del club («View Profile» vale per i
 * soci): questa finestra ne mostra i dati con cui si è iscritto.
 *
 * I dati si chiedono AL CLIC, non all'apertura della pagina: GET
 * /clinic-events/:id/participants/:regId/guest (ADMIN/SUPERADMIN), che
 * risponde con un elenco chiuso — tipo, nome, cognome, data di nascita; il
 * tutore (bambino) o l'email (adulto); registrazione, termini, porta.
 *
 * Testi a schermo in inglese (CLAUDE.md §3.7).
 */

// Il tipo a schermo: «Child», «Adult — Player», «Adult — Coach».
function tipoAschermo(scheda) {
  if (scheda.type !== "adult") return "Child";
  if (scheda.joiningAs === "COACH") return "Adult — Coach";
  if (scheda.joiningAs === "PLAYER") return "Adult — Player";
  return "Adult";
}

// La porta a schermo.
function origineAschermo(origin) {
  if (origin === "clinic-public-link") return "Public link";
  if (origin === "clinic-admin-add") return "Added by admin";
  return "—";
}

// Una data di calendario (AAAA-MM-GG) scritta per esteso, senza passare dal
// fuso della macchina: si scompone la stringa e si ricompone in UTC, come
// `dataLeggibile` della pagina dell'evento.
function dataDiCalendario(iso) {
  const pezzi = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!pezzi) return iso ? String(iso) : "—";

  const quando = new Date(
    Date.UTC(Number(pezzi[1]), Number(pezzi[2]) - 1, Number(pezzi[3]))
  );

  return quando.toLocaleDateString("en-AU", {
    timeZone: "UTC",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// Un istante (ISO del server) sul calendario del club, Perth. Con l'ora se
// richiesta. Un valore che non è una data si mostra com'è.
function istante(iso, conOra) {
  if (!iso) return null;
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return String(iso);

  return quando.toLocaleString("en-AU", {
    timeZone: "Australia/Perth",
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(conOra ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

// Una riga della scheda: etichetta a sinistra, valore a destra.
const Riga = ({ etichetta, valore }) => (
  <div className="qpsc-clinic__guest-field">
    <span className="qpsc-clinic__guest-label">{etichetta}</span>
    <span className="qpsc-clinic__guest-value">{valore || "—"}</span>
  </div>
);

export default function ClinicGuestModal({ idEvento, iscritto, onClose }) {
  const [scheda, setScheda] = useState(null);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const risposta = await api.get(
          `/clinic-events/${idEvento}/participants/${iscritto.id}/guest`
        );
        if (vivo) setScheda(risposta || null);
      } catch (err) {
        if (vivo) {
          setErrore(
            messaggioDelServer(err) || "The guest details could not be loaded."
          );
        }
      }
    })();

    return () => {
      vivo = false;
    };
  }, [idEvento, iscritto.id]);

  const nome = `${iscritto.childFirstName || ""} ${iscritto.childLastName || ""}`.trim();

  return (
    <div className="qpsc-modal-overlay">
      <div className="qpsc-modal" role="dialog" aria-modal="true">
        <h2 className="qpsc-modal__title">Guest details for {nome}</h2>

        <div className="qpsc-modal__body">
          {errore && (
            <p className="qpsc-clinic__error" role="alert">
              {errore}
            </p>
          )}

          {!errore && !scheda && <p className="qpsc-clinic__hint">Loading…</p>}

          {scheda && (
            <>
              <section className="qpsc-pay-card">
                <div className="qpsc-pay-card__head">
                  <h3 className="qpsc-pay-card__title">
                    {scheda.type === "adult" ? "Guest" : "Child"}
                  </h3>
                </div>
                <Riga etichetta="Type" valore={tipoAschermo(scheda)} />
                <Riga etichetta="First name" valore={scheda.firstName} />
                <Riga etichetta="Last name" valore={scheda.lastName} />
                <Riga etichetta="Date of birth" valore={dataDiCalendario(scheda.dob)} />
                {scheda.type === "adult" && (
                  <Riga etichetta="Email" valore={scheda.email} />
                )}
              </section>

              {scheda.type !== "adult" && scheda.guardian && (
                <section className="qpsc-pay-card">
                  <div className="qpsc-pay-card__head">
                    <h3 className="qpsc-pay-card__title">Parent / guardian</h3>
                  </div>
                  <Riga etichetta="First name" valore={scheda.guardian.firstName} />
                  <Riga etichetta="Last name" valore={scheda.guardian.lastName} />
                  <Riga etichetta="Email" valore={scheda.guardian.email} />
                </section>
              )}

              <section className="qpsc-pay-card">
                <div className="qpsc-pay-card__head">
                  <h3 className="qpsc-pay-card__title">Enrolment</h3>
                </div>
                <Riga
                  etichetta="Enrolled on"
                  valore={istante(scheda.registeredAt, false)}
                />
                <Riga
                  etichetta="Terms accepted"
                  valore={
                    istante(scheda.acceptedTermsAt, true) || "Not accepted in the app"
                  }
                />
                <Riga etichetta="Origin" valore={origineAschermo(scheda.origin)} />
              </section>
            </>
          )}
        </div>

        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
